import type { IsoTimestamp } from './common.js';

// Tools → HTML Hosting. A staff member uploads a self-contained HTML report and
// gets a shareable client URL. The HTML FILE lives in Google Cloud Storage (a
// report with embedded charts routinely exceeds Firestore's 1MB document
// limit); this doc is the metadata + lifecycle around it.
//
// ⚠️ The security model is ORIGIN ISOLATION, not sanitisation — see
// server/modules/hostedReports/publicRouter.ts. Nothing about the storage
// shape is load-bearing for that; the CSP on the serving response is.

/**
 * ⚠️ `html` (one self-contained file) is the original shape and what every doc
 * without this field is. `bundle` is a .zip of a page plus its folders,
 * extracted to object storage; see server/modules/hostedReports/bundleExtract.ts.
 */
export type HostedReportKind = 'html' | 'bundle';

/**
 * A bundle is never viewable before extraction finishes: `uploading` (the zip is
 * on its way or waiting for finalise), `extracting` (finalise is running or was
 * cut by its time budget and will resume), `ready`.
 */
export type HostedBundleStatus = 'uploading' | 'extracting' | 'ready';

/** What the doc keeps of a bundle: the summary, not the file list. */
export type HostedBundleSummary = {
  entryPath: string;
  fileCount: number;
  totalBytes: number;
  extractedAt: IsoTimestamp;
};

export type HostedReport = {
  id: string;
  /** URL-safe token — the only credential the client link carries. */
  token: string;
  title: string;
  /** Optional link to a client Account so reports can be filtered by client. */
  accountId: string | null;
  /**
   * Denormalised at create so the list renders without an N-account join. A
   * later account rename leaves this stale, which is acceptable for a label on
   * a point-in-time artefact (and the accountId still resolves).
   */
  accountName: string | null;
  /** GCS object path. Never public — always served through our CSP'd route. */
  storagePath: string;
  sizeBytes: number;
  createdAt: IsoTimestamp;
  createdBy: string;
  /** Denormalised creator name — everyone sees all reports, with whose it is. */
  createdByName: string;
  createdByEmail: string;
  /**
   * When the link stops working. ⚠️⚠️ NULL MEANS NEVER (2026-09-23) — a real
   * state, not a missing value. It is stored as null rather than a far-future
   * sentinel because a sentinel is a magic value: it would surface on screen as
   * "expires 31 Dec 9999" and put two truths behind one field.
   *
   * ⚠️ Every reader must treat null as "never expires", and the one that can
   * destroy files — the cleanup selector — must treat it as "never eligible
   * unless revoked". Coercing it to 0 anywhere reads as "expired in 1970".
   */
  expiresAt: IsoTimestamp | null;
  /**
   * scrypt hash of the optional viewer password, in the format produced by
   * server/lib/passwordHash.ts. NEVER the plaintext, and NEVER sent to any
   * client — the wire type below omits it entirely.
   */
  passwordHash: string | null;
  revokedAt: IsoTimestamp | null;
  /**
   * When the FILE behind this link was last replaced (null = never). The list
   * shows it so "the client is looking at an old version" is answerable.
   *
   * ⚠️ A replace does NOT reset viewCount: the count belongs to the LINK, which
   * is the thing that was shared and never changes. Zeroing it on replace would
   * silently destroy the history of a link that is still the same link.
   */
  lastReplacedAt: IsoTimestamp | null;
  /**
   * ⚠️⚠️ THE PORTAL MARKER (2026-09-23, Amendment 2 §A). Non-null when a portal
   * library tile points at this report, and then THE EXPIRY IS NOT THIS
   * MODULE'S TO CHANGE: it follows the tile's `visibleUntil`, and an expiry
   * edit here is refused with a 409 naming the entry. That is what keeps "live
   * tile, dead link" unrepresentable from every door, not just the upload form.
   *
   * ⚠️ AN ID, NOT A BOOLEAN. A boolean that went stale (a tile deleted, the
   * clear half-failed) would lock the report out of expiry edits forever with
   * nothing to say why. An id names the entry, and when that entry no longer
   * exists the edit is let through and the stale id cleared.
   *
   * ⚠️ OPAQUE to this module: a string it was handed. It never reads the
   * library; existence is answered by a lookup the app wires in at boot.
   * Legacy docs have no field; they read as null.
   */
  portalLibraryEntryId: string | null;
  /** Absent on legacy docs; normalised to 'html' on read. */
  kind: HostedReportKind;
  /** null for an html report. */
  bundleStatus: HostedBundleStatus | null;
  bundle: HostedBundleSummary | null;
  /** SERVER-ONLY: the object prefix the bundle was extracted under. */
  bundlePrefix: string | null;
  /** SERVER-ONLY: epoch ms until which a finalise holds the extraction lease. */
  bundleLeaseUntilMs: number | null;
  /** Doc-only view tracking (not audited) — the previewLinks posture. */
  viewCount: number;
  lastViewedAt: IsoTimestamp | null;
};

/**
 * What the authed management UI receives. `passwordHash` is replaced by a
 * boolean: the UI needs to show a lock icon, never the hash. Omitting it here
 * (rather than trusting each call site to strip it) is what makes the leak
 * impossible to reintroduce by forgetting.
 */
export type HostedReportSummary = Omit<
  HostedReport,
  'passwordHash' | 'storagePath' | 'bundlePrefix' | 'bundleLeaseUntilMs'
> & {
  hasPassword: boolean;
  /** Absolute shareable URL, composed server-side off PUBLIC_BASE_URL. */
  url: string;
};

/** Lifecycle state, evaluated at READ time — no expiry cron. */
export type HostedReportState = 'ok' | 'revoked' | 'expired';

/**
 * What the PUBLIC viewer is told. Deliberately minimal.
 *
 * `password_required` carries NO title: whoever holds the URL but not the
 * password should not learn which client the report is about. Once unlocked
 * they can read the whole thing, so the title is free from that point on.
 */
export type PublicHostedReportStatus =
  | { status: 'not_found' }
  | { status: 'revoked' }
  /** ⚠️ Non-null by construction: a link that never expires cannot be expired. */
  | { status: 'expired'; expiresAt: IsoTimestamp }
  | { status: 'password_required' }
  /** `expiresAt: null` = the link never expires. */
  | { status: 'ok'; title: string; expiresAt: IsoTimestamp | null };

export const HOSTED_REPORT_TOKEN_LENGTH = 32;
/**
 * The create form's default expiry. 90 since 2026-09-23 (was 30) — Scott's
 * request. ⚠️ NEW UPLOADS ONLY: existing reports keep their stored dates, and
 * nothing re-applies this to them.
 */
export const HOSTED_REPORT_DEFAULT_EXPIRY_DAYS = 90;
/**
 * 25 MB — generous for a self-contained report with embedded charts, and well
 * inside Cloud Run's hard 32 MiB request-body limit even after JSON escaping
 * (see the upload router's parser limit).
 */
export const HOSTED_REPORT_MAX_BYTES = 25 * 1024 * 1024;
